import { useState, useEffect } from 'react';
import { servicesApi } from '@/lib/api';

interface ServiceTagData {
    bestFor: string[];
    integrations: string[];
    workerFunctions: string[];
}

export const useServiceTags = (serviceId?: string) => {
    const [tagData, setTagData] = useState<ServiceTagData>({
        bestFor: [],
        integrations: [],
        workerFunctions: [],
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!serviceId) {
            setLoading(false);
            return;
        }

        const fetchServiceTags = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await servicesApi.getById(serviceId);

                // Handle different response formats
                let service: any;
                if (response && typeof response === 'object') {
                    if ('service' in response) {
                        service = (response as { service?: any }).service;
                    } else if ('data' in response) {
                        service = (response as { data?: any }).data;
                    } else {
                        service = response;
                    }
                }

                if (!service) {
                    throw new Error('Service not found');
                }

                // Extract tags from tagRelations
                const tagRelations = service.tagRelations || [];
                
                const bestForTags: string[] = [];
                const integrationTags: string[] = [];
                const workerFunctionTags: string[] = [];

                tagRelations.forEach((relation: any) => {
                    const tag = relation.tag || relation.serviceTag;
                    if (!tag) return;

                    const tagType = tag.type;
                    const tagValue = tag.value;

                    if (!tagValue) return;

                    switch (tagType) {
                        case 'best_for':
                            bestForTags.push(tagValue);
                            break;
                        case 'integration':
                            integrationTags.push(tagValue);
                            break;
                        case 'worker_function':
                            workerFunctionTags.push(tagValue);
                            break;
                    }
                });

                setTagData({
                    bestFor: bestForTags,
                    integrations: integrationTags,
                    workerFunctions: workerFunctionTags,
                });
            } catch (err) {
                console.error('Failed to fetch service tags:', err);
                setError(err instanceof Error ? err.message : 'Failed to load service tags');
            } finally {
                setLoading(false);
            }
        };

        fetchServiceTags();
    }, [serviceId]);

    return { tagData, loading, error };
};
